/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import Snackbar from "@atg/icf-react-components/components/Snackbar";
import isEmpty from "lodash/fp/isEmpty";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";

import ConfirmDialog from "~/components/modal/ConfirmDialog";
import { selectMessagesData } from "~/features/messages/selectors";
import { MessagesActions } from "~/features/messages/slice";
import { MessagesType } from "~/features/messages/types";
import { AppDispatch } from "~/state/types";

const AUTO_HIDE_DURATION = 4000;

const useStyles = createUseStyles({
  centerVertically: {
    alignItems: "center",
    display: "flex",
    height: "10vh",
    justifyContent: "center",
    top: "50%",
  },
});

const Messages: FC = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch<AppDispatch>();

  const [isOpen, setIsOpen] = useState(false);

  const messagesData = useSelector(selectMessagesData);
  const hasMessage = !isEmpty(messagesData.message);

  const timer = useRef(0);

  const onCloseClick = () => {
    setIsOpen(false);
    timer.current = window.setTimeout(() => {
      dispatch(MessagesActions.resetMessage());
    }, 300);
  };

  useEffect(() => {
    if (hasMessage) {
      setIsOpen(true);

      if (messagesData.type === MessagesType.SNACK) {
        timer.current = window.setTimeout(() => {
          onCloseClick();
        }, AUTO_HIDE_DURATION);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesData]);

  useEffect(() => {
    return () => {
      window.clearTimeout(timer.current);
    };
  }, []);

  const classes = useStyles();

  if (messagesData.type === MessagesType.SNACK) {
    return (
      <Snackbar
        autoHideDuration={AUTO_HIDE_DURATION}
        message={messagesData.message}
        open={isOpen}
        onClose={onCloseClick}
        className={messagesData.centered ? classes.centerVertically : ""}
      />
    );
  }

  return (
    <ConfirmDialog
      open={isOpen}
      title={messagesData.title}
      confirmLabel={t("ok")}
      content={messagesData.message}
      confirmClick={onCloseClick}
    />
  );
};

export default Messages;
